/**
 * Browser-translate DOM crash patch.
 *
 * Chrome's built-in translator and the Google Translate extension wrap every
 * translated text node in a `<font>` element. When React later reconciles a
 * subtree whose text siblings have been re-parented this way, it calls
 * `removeChild` / `insertBefore` on a parent that no longer owns those
 * children and throws `NotFoundError: Failed to execute 'removeChild' on
 * 'Node': The node to be removed is not a child of this node.` There is no
 * React error boundary for this — the throw happens inside the commit phase
 * and unmounts the whole page.
 *
 * This module patches `Node.prototype.removeChild` and
 * `Node.prototype.insertBefore` so the exact failure mode becomes a graceful
 * delegate / `appendChild` fallback instead of throwing. The patched path is
 * only taken when the native call would have thrown; normal reconciliation
 * is untouched. This is the long-standing community workaround for
 * facebook/react#11538.
 *
 * The `insertBefore` fallback cannot keep order, so components should wrap
 * text that sits next to conditional content in its own element. Fallbacks
 * are reported to Datadog RUM once per method and parent tag per page load.
 *
 * The module is a no-op server-side (`typeof window` guard) and idempotent
 * across hot reloads (window-scoped flag). It applies on import.
 */
import { datadogRum } from '@datadog/browser-rum';

const PATCH_FLAG = '__arkestroBrowserTranslateDomPatchApplied__';

export const BROWSER_TRANSLATE_DOM_PATCH_ACTION = 'browser_translate_dom_patch';

type PatchableWindow = Window & { [PATCH_FLAG]?: boolean };

const reportedFallbacks = new Set<string>();

export const resetBrowserTranslateDomPatchReports = (): void => {
  reportedFallbacks.clear();
};

const reportFallback = (method: 'insertBefore' | 'removeChild', parent: Node, node: Node) => {
  const key = `${method}:${parent.nodeName}`;
  if (reportedFallbacks.has(key)) return;
  reportedFallbacks.add(key);
  datadogRum.addAction(BROWSER_TRANSLATE_DOM_PATCH_ACTION, {
    method,
    nodeName: node.nodeName,
    parentNodeName: parent.nodeName,
  });
};

export const applyBrowserTranslateDomPatch = (): void => {
  if (typeof window === 'undefined') return;

  const w = window as PatchableWindow;
  if (w[PATCH_FLAG]) return;
  w[PATCH_FLAG] = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function patchedRemoveChild<T extends Node> (
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      reportFallback('removeChild', this, child);
      if (child.parentNode) {
        return child.parentNode.removeChild(child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchedInsertBefore<T extends Node> (
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportFallback('insertBefore', this, newNode);
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};

applyBrowserTranslateDomPatch();
